import { createContext, useContext, type ReactNode } from "react";
import { Box, Card, Group, SimpleGrid, Skeleton, Stack, Text, Tooltip, type SimpleGridProps } from "@mantine/core";
import { IconArrowDownRight, IconArrowUpRight, IconMinus, type TablerIcon } from "@tabler/icons-react";
import { formatPercent, relativeChange } from "../../lib/format";

const InGroup = createContext(false);

/**
 * One panel holding several KPIs, split by 1px hairlines — instead of a row
 * of separate boxed cards, each with its own icon chip.
 */
export function KpiGroup({ children, cols, mb }: { children: ReactNode; cols: SimpleGridProps["cols"]; mb?: string }) {
  return (
    <Card padding={0} mb={mb} style={{ overflow: "hidden" }}>
      {/* Cells draw right/bottom hairlines; the card clips the outer edges. */}
      <SimpleGrid cols={cols} spacing={0} verticalSpacing={0} style={{ marginRight: -1, marginBottom: -1 }}>
        <InGroup.Provider value>{children}</InGroup.Provider>
      </SimpleGrid>
    </Card>
  );
}

/**
 * A headline number with its change against a comparison value. `goodWhen`
 * decides the colour (cancellations going up is bad); the arrow and the signed
 * percentage carry the meaning too, so colour is never the only cue.
 */
export function KpiCard({
  label,
  value,
  current,
  previous,
  compareLabel,
  goodWhen = "up",
  hint,
  loading,
  onClick,
  footer,
}: {
  label: string;
  value: ReactNode;
  current?: number | null;
  previous?: number | null;
  compareLabel?: string;
  goodWhen?: "up" | "down" | "neutral";
  /** Kept for call-site compatibility; KPI tiles no longer show icon chips. */
  icon?: TablerIcon;
  hint?: string;
  loading?: boolean;
  onClick?: () => void;
  footer?: ReactNode;
}) {
  const inGroup = useContext(InGroup);
  const change = relativeChange(current, previous);
  const dir = change === null || Math.abs(change) < 0.005 ? "flat" : change > 0 ? "up" : "down";
  const good = goodWhen === "neutral" || dir === "flat" ? null : (dir === "up") === (goodWhen === "up");
  const color = good === null ? "var(--text-tertiary)" : good ? "var(--success-fg)" : "var(--error-fg)";
  const Arrow = dir === "up" ? IconArrowUpRight : dir === "down" ? IconArrowDownRight : IconMinus;

  const content = loading ? (
    <Stack gap={10}>
      <Skeleton h={12} w="55%" />
      <Skeleton h={26} w="70%" />
      <Skeleton h={10} w="40%" />
    </Stack>
  ) : (
    <Stack gap={6}>
      <Text size="sm" c="var(--text-secondary)" fw={500} lh={1.3}>
        {label}
      </Text>
      <Text fw={600} fz={26} lh={1.15} className="num" style={{ letterSpacing: "-0.02em" }}>
        {value}
      </Text>
      {(change !== null || compareLabel) && (
        <Group gap={6} wrap="nowrap">
          {change !== null && (
            <Text size="xs" fw={600} c={color} className="num" span style={{ whiteSpace: "nowrap" }}>
              <Arrow size={12} style={{ verticalAlign: "-1px" }} aria-hidden />
              {change > 0 ? "+" : ""}
              {formatPercent(change)}
            </Text>
          )}
          {compareLabel && (
            <Text size="xs" c="dimmed" span lineClamp={1}>
              {compareLabel}
            </Text>
          )}
        </Group>
      )}
      {footer}
    </Stack>
  );

  const interactive = onClick
    ? {
        onClick,
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => (e.key === "Enter" || e.key === " ") && onClick(),
        className: "kpi-cell clickable",
      }
    : { className: "kpi-cell" };

  const body = inGroup ? (
    <Box p="md" h="100%" {...interactive} style={{ boxShadow: "inset -1px -1px 0 var(--border-subtle)" }}>
      {content}
    </Box>
  ) : (
    <Card padding="md" h="100%" {...interactive}>
      {content}
    </Card>
  );

  return hint ? (
    <Tooltip label={hint} multiline w={260}>
      {body}
    </Tooltip>
  ) : (
    body
  );
}
